/**
 * The warning strip along the bottom.
 *
 * Advice, never a wall (§6.3). It reports what would print badly and offers to
 * fix it; it does not stop the order. The one thing it must never do is be
 * absent - a customer who has never seen this strip does not know the editor
 * was checking.
 *
 * The real preflight is phase 5 and runs on the server. What runs here is the
 * subset that can be answered from the document alone, so the customer hears
 * about it while they can still do something.
 */

import type { TrimboxDocument } from '../model/types';
import { flatten } from '../model/types';
import { knownAsset } from '../assets';
import { blocking, preflight, type Finding } from '../press/preflight';
import { useEditor } from '../state/store';
import { IconReady, IconWarn } from './icons';

export type { Finding };

/**
 * What the editor can check, which is everything except the fonts.
 *
 * A missing font is a shop problem and the render worker is the one that finds
 * out - here every font in the menu is by definition one we have.
 */
export function check(doc: TrimboxDocument): Finding[] {
  return preflight(doc, {
    asset: (id) => {
      const asset = knownAsset(id);

      return asset ? { w: asset.w, h: asset.h } : null;
    },
  });
}

export { blocking };

export function PreflightBar() {
  const doc = useEditor((s) => s.doc);
  const pageIndex = useEditor((s) => s.pageIndex);
  const select = useEditor((s) => s.select);
  const goToPage = useEditor((s) => s.goToPage);

  if (!doc) return null;

  const findings = check(doc);

  if (findings.length === 0) {
    return (
      <div className="tb-preflight tb-preflight--ready">
        <IconReady />
        <span className="tb-preflight__text">Ready to print.</span>
      </div>
    );
  }

  const worst = findings.some((f) => f.level === 'stop') ? 'stop' : 'warn';
  const first = findings[0]!;

  return (
    <div className={`tb-preflight tb-preflight--${worst}`}>
      <IconWarn />
      <span className="tb-preflight__text">{first.message}</span>

      {findings.length > 1 && (
        <span className="tb-preflight__count">+{findings.length - 1} more</span>
      )}

      {first.layerId && (
        <button
          type="button"
          className="tb-btn tb-btn--quiet"
          onClick={() => {
            const page = doc.pages.findIndex((p) => flatten(p.layers).some((l) => l.id === first.layerId));

            if (page >= 0 && page !== pageIndex) goToPage(page);

            select([first.layerId!]);
          }}
        >
          Show me
        </button>
      )}
    </div>
  );
}
